import { register } from "./registry";
import { focusField } from "./keyboard";

interface MenuItem {
  code: string;
  title: string;
  url: string;
  path: string;
  searchStr: string;
}
let cachedItems: MenuItem[] = [];
async function loadItems() {
  try {
    const r = await fetch("/api/v1/menu/search");
    if (r.ok) {
      const data = await r.json() as { code: string; title: string; url: string; path: string; aliases?: string[] }[];
      cachedItems = data.map(d => ({
        ...d,
        searchStr: `${d.title} ${d.path} ${d.code} ${(d.aliases ?? []).join(" ")}`.toLowerCase()
      }));
    }
  } catch { /* ignored */ }
}
loadItems();

/** Ctrl+K menu search by new and old (CargoBooks) names (06 §6, UX-04). */
let bound = false;
register(() => {
  if (bound) return;
  bound = true;
  const dlg = document.createElement("dialog"); dlg.id = "kit-palette"; dlg.className = "kit-modal";
  dlg.setAttribute("data-kit-palette", "");
  dlg.setAttribute("aria-label", "Search screens");
  const input = document.createElement("input"); input.placeholder = "Go to screen…"; input.setAttribute("aria-label", "Menu search");
  const ul = document.createElement("ul"); ul.id = "kit-palette-list"; ul.setAttribute("role", "listbox"); ul.setAttribute("aria-label", "Screens");
  // combobox: the keys stay in the box, and aria-activedescendant tells a screen reader which screen Enter opens
  Object.entries({ role: "combobox", "aria-controls": ul.id, "aria-autocomplete": "list", "aria-expanded": "false" })
    .forEach(([k, v]) => input.setAttribute(k, v));
  dlg.append(input, ul);
  document.body.appendChild(dlg);

  let timer = 0;
  let sel = 0;

  function select(i: number) {
    sel = i;
    const lis = Array.from(ul.querySelectorAll("li"));
    lis.forEach((li, n) => li.setAttribute("aria-selected", String(n === sel)));
    input.setAttribute("aria-expanded", String(lis.length > 0));
    if (lis[sel]) input.setAttribute("aria-activedescendant", lis[sel]!.id); else input.removeAttribute("aria-activedescendant");
  }

  function renderItems(items: { url: string; path: string }[]) {
    ul.replaceChildren(...items.map((it, i) => {
      const li = document.createElement("li");
      li.id = `kit-palette-${i}`;
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", String(i === 0));
      li.dataset.url = it.url;
      li.textContent = `${it.path}`;
      return li;
    }));
    select(0);
  }

  function updateList(q: string) {
    if (!q) { ul.replaceChildren(); select(0); return; }
    if (cachedItems.length > 0) {
      const words = q.toLowerCase().split(/\s+/).filter(w => w.length > 0 && w !== "▸");
      const ql = q.toLowerCase();
      // Same order as the server (MenuSearch): exact title, then title prefix, then any match — so Enter on "Trial Balance" never picks a longer namesake.
      const rank = (it: MenuItem) => it.title.toLowerCase() === ql ? 0 : it.title.toLowerCase().startsWith(ql) ? 1 : 2;
      const matches = cachedItems.filter(it => words.every(w => it.searchStr.includes(w)))
        .map((it, i) => ({ it, i })).sort((a, b) => rank(a.it) - rank(b.it) || a.i - b.i).map(x => x.it);
      renderItems(matches);
    }
  }

  input.addEventListener("input", () => {
    const q = input.value.trim();
    updateList(q);
    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const r = await fetch(`/api/v1/menu/search?q=${encodeURIComponent(q)}`);
      if (!r.ok) return;
      const items = await r.json() as { url: string; path: string }[];
      if (items.length) renderItems(items);
    }, 50);
  });

  input.addEventListener("keydown", (e) => {
    const lis = Array.from(ul.querySelectorAll("li"));
    if (e.key === "ArrowDown" && lis.length) { e.preventDefault(); select(Math.min(sel + 1, lis.length - 1)); return; }
    if (e.key === "ArrowUp" && lis.length) { e.preventDefault(); select(Math.max(sel - 1, 0)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      const navigateTo = (url: string) => {
        dlg.close(); input.value = ""; ul.replaceChildren();
        if (location.pathname + location.search === url || location.href === url) {
          document.body.dispatchEvent(new CustomEvent("kit:reset"));
          const first = document.querySelector<HTMLElement>("[data-kit-form] [data-kit-order]") ?? document.querySelector<HTMLElement>("[data-kit-order]");
          if (first) focusField(first);
        } else {
          location.href = url;
        }
      };
      if (lis.length && lis[sel]) {
        const url = lis[sel]!.dataset.url;
        if (url) { navigateTo(url); return; }
      }
      const q = input.value.trim();
      if (q && cachedItems.length) {
        const words = q.toLowerCase().split(/\s+/).filter(w => w.length > 0 && w !== "▸");
        const match = cachedItems.find(it => words.every(w => it.searchStr.includes(w)));
        if (match) { navigateTo(match.url); return; }
      }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); dlg.close(); input.value = ""; ul.replaceChildren(); return; }
  });

  document.addEventListener("keydown", (e) => {
    if (e.ctrlKey && (e.key === "k" || e.key === "K")) {
      e.preventDefault();
      if (dlg.open) { dlg.close(); return; }
      if (cachedItems.length === 0) loadItems();
      sel = 0;
      dlg.showModal();
      input.focus();
    }
  });
});
